import {
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useState,
} from "react";
import { useAtom, useAtomValue } from "jotai";
import { Alert, Button } from "reactstrap";

import {
  ActionsCloseButton,
  FloatingToolbar,
  IconAction,
} from "../PageView/PageViewTable/SharedToolbar/SharedSelectedRecordsActions";
import useBulkPageUsers, {
  BulkUsersPayload,
} from "../../api/api-hooks/pages/useBulkPageUsers";
import useTransferPageOwnership from "../../api/api-hooks/pages/useTransferPageOwnership";
import useUpdatePageSettings from "../../api/api-hooks/pages/useUpdatePageSettings";
import useUpdatePageUser from "../../api/api-hooks/pages/useUpdatePageUser";
import useDeletePageUser from "../../api/api-hooks/pages/useDeletePageUser";
import useWorkspacePermissions from "../../hooks/useWorkspacePermissions";
import ManageUserPermissionsDrawer from "./ManageUserPermissionsDrawer";
import BulkdUsersPermissionsDrawer from "./BulkUsersPermissionsDrawer";
import { Page, PageView, Person } from "../../types/GlobalInterfaces";
import DropdownOptionWithIcon from "../Menus/DropdownOptionWithIcon";
import useUpdatePage from "../../api/api-hooks/pages/useUpdatePage";
import useDeletePage from "../../api/api-hooks/pages/useDeletePage";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import usePagePermissions from "../../hooks/usePagePermissions";
import TransferOwnershipDrawer from "./TransferOwnershipDrawer";
import PopoverDropdownMenu from "../Menus/PopoverDropdownMenu";
import { workspaceIdAtom } from "../../atoms/WorkspaceAtoms";
import { getUserPermissionID } from "../../utils/UserUtils";
import { isGlobalCloudAtom, tenantIDAtom } from "../../atoms/GlobalAtoms";
import PageEditor from "../Sidebar/SidebarAdd/PageEditor";
import InvitePageUserDrawer from "./InvitePageUserDrawer";
import { PageSettingsPage } from "../../atoms/PageAtoms";
import AppEmptyState from "../../elements/AppEmptyState";
import useUser from "../../api/api-hooks/auth/useUser";
import usePagePeople from "../../hooks/usePagePeople";
import AppSpinner from "../../elements/AppSpinner";
import IconButton from "../../elements/IconButton";
import ListItemCheck from "../CRM/ListItemCheck";
import AppNavbar from "../AppNavbar/AppNavbar";
import useToggle from "../../hooks/useToggle";
import StrUtils from "../../utils/StrUtils";
import ListItem from "../CRM/ListItem";
import AppDrawer from "./AppDrawer";

const tabs = [
  { title: "Views", activeValue: "views" },
  { title: "Members", activeValue: "members" },
  { title: "Settings", activeValue: "settings" },
];

const PageViews = ({
  views,
  pageID,
  isInstalledGlobalApp,
}: {
  views: PageView[];
  pageID: string;
  isInstalledGlobalApp: boolean;
}) => {
  const { cPermissions } = useCloudPermissions();
  const { updatePage } = useUpdatePage();

  if (!views?.length) {
    return (
      <AppEmptyState
        title="This page has no Views"
        description={`This page doesn't have any Views added yet.`}
        hideIcon
      />
    );
  }

  const handleHideViews = (viewID: string) => {
    const newViews = [...views];
    const viewIndex = newViews?.findIndex((view) => view.id === viewID);

    if (viewIndex < 0) return;

    const newView = {
      ...newViews[viewIndex],
      Hide: !newViews[viewIndex]?.Hide,
    };

    newViews[viewIndex] = newView;

    updatePage({
      pageID: pageID,
      // @ts-ignore
      payload: {
        payload: { Views: newViews },
      },
    });
  };

  const showIcon = isInstalledGlobalApp
    ? false
    : cPermissions.cloud_modify_permissions;

  return (
    <>
      {views?.length &&
        views?.map((view) => {
          return (
            <ListItem
              key={view.id}
              title={view.Name}
              icon={"mdi-newspaper-variant-outline"}
              optionsButton={
                !showIcon ? undefined : (
                  <div className="tw:flex">
                    <IconButton
                      onClick={() => handleHideViews(view.id)}
                      icon={view.Hide ? "eye-off" : "eye"}
                      tooltipText={view.Hide ? "Unhide" : "Hide"}
                      className="mx-1"
                    />
                  </div>
                )
              }
            />
          );
        })}
    </>
  );
};

const PageMemberOptions = ({
  member,
  pPermissions,
  wPermissions,
  isOwner,
  isCurrentUser,
  setManageUser,
  setTransferUser,
  removeMember,
}: {
  member: any[];
  pPermissions: any;
  wPermissions: any;
  isOwner: boolean;
  isCurrentUser: boolean;
  setManageUser: Dispatch<SetStateAction<any>>;
  setTransferUser: Dispatch<SetStateAction<any>>;
  removeMember: (member: any) => void;
}) => {
  return (
    <PopoverDropdownMenu>
      <DropdownOptionWithIcon
        name="Manage Permissions"
        onClick={() => setManageUser(member)}
        icon="mdi mdi-pencil"
        disabled={
          isOwner ||
          isCurrentUser ||
          (!pPermissions.page_modify_user_permissions &&
            !wPermissions.workspace_modify_user_permissions_page)
        }
      />
      <DropdownOptionWithIcon
        name="Set As Owner"
        onClick={() => setTransferUser(member)}
        icon="mdi mdi-crown"
        disabled={
          isOwner ||
          (!pPermissions.page_transfer_ownership &&
            !wPermissions.workspace_transfer_page_ownership)
        }
      />
      <DropdownOptionWithIcon
        name="Remove Member"
        onClick={() => removeMember(member)}
        icon="mdi mdi-delete"
        disabled={
          (!pPermissions.page_delete_user &&
            !wPermissions.workspace_delete_user_page) ||
          isOwner ||
          isCurrentUser
        }
      />
    </PopoverDropdownMenu>
  );
};

const PageMembers = ({
  page,
  pPermissions,
  wPermissions,
  disabled = false,
  cloudID,
}: {
  page: Page;
  pPermissions: any;
  wPermissions: any;
  disabled: boolean;
  cloudID: string;
}) => {
  const members = page?.Members || [];
  const pagePeople: any[] = usePagePeople(members);
  const [workspaceID] = useAtom(workspaceIdAtom);
  const [manageUser, setManageUser] = useState(null);
  const [transferUser, setTransferUser] = useState<Person | null>(null);
  const { user, pPermissions: pagePermissions } = useUser();
  const currentOwnerID = page?.Owner || "";
  const { updatePageUser, updatingUser, updatePageUserError } =
    useUpdatePageUser(page?.id || "");
  const { transferOwnership, transferringOwnership, transferOwnershipError } =
    useTransferPageOwnership(page?.id || "");
  const { deleteUser } = useDeletePageUser(page?.id || "");
  const { bulkPageUsers, updating, updateBulkError } = useBulkPageUsers(
    page?.id || ""
  );
  const [selectedElements, setSelectedElements] = useState<string[]>([]);
  const [bulkPermissions, setBulkPermissions] = useState<string[]>([]);
  const [message, setMessage] = useState<{
    message: string;
    type: "success" | "danger";
  } | null>(null);

  const removeMember = (member: any) => {
    deleteUser({
      workspaceID: workspaceID ?? "",
      pageID: page.id ?? "",
      userID: member.id,
    });
  };

  const handleUpdateUser = (payload: Record<string, any>) => {
    //@ts-ignore
    updatePageUser({
      workspaceID: workspaceID ?? "",
      pageID: page?.id ?? "",
      ...payload,
    });
  };

  const handleBulkUsers = (
    payload: BulkUsersPayload,
    onSuccess?: () => void
  ) => {
    bulkPageUsers({
      workspaceID: workspaceID ?? "",
      pageID: page?.id ?? "",
      payload,
      onSuccess: (message) => {
        onSuccess && onSuccess();
        setMessage({ message, type: "success" });
      },
    });
  };

  const handleTransferOwnership = (payload: Record<string, any>) => {
    //@ts-ignore
    transferOwnership({
      currentOwnerID: currentOwnerID,
      workspaceID: workspaceID ?? "",
      pageID: page?.id ?? "",
      ...payload,
    });
  };

  const handleGetPermissionID = useCallback(
    (user: Person) => {
      return getUserPermissionID(user, "Pages", page?.id);
    },
    [page?.id]
  );

  if (!pagePeople?.length || !members?.length)
    return (
      <AppEmptyState
        title="No members found."
        description={`Something went wrong, unable to find the page members.`}
        hideIcon
      />
    );

  return (
    <>
      {updateBulkError && <Alert color="danger"> {updateBulkError}</Alert>}
      {message && (
        <Alert color={message?.type} toggle={() => setMessage(null)}>
          {message?.message}
        </Alert>
      )}
      {pagePeople?.length &&
        pagePeople?.map((member) => {
          const isOwner = member?.Owner;
          const isCurrentUser = member.id === user.id;
          const permissions = member.PagePermissions;

          return (
            <div className="tw:border-b-1 tw:border-lighter">
              <Fragment key={member.id}>
                <ListItemCheck
                  title={member.Name + `${isCurrentUser ? " (Me)" : ""}`}
                  subTitle={member?.PrimaryContactEmail}
                  icon={isOwner ? "mdi-crown" : "mdi-account-outline"}
                  badgeTitle={permissions?.Name}
                  badgeColor={permissions?.Owner ? "secondary" : "light"}
                  options={
                    disabled ? null : (
                      <PageMemberOptions
                        member={member}
                        pPermissions={pPermissions}
                        wPermissions={wPermissions}
                        isOwner={isOwner}
                        isCurrentUser={isCurrentUser}
                        setManageUser={setManageUser}
                        setTransferUser={setTransferUser}
                        removeMember={removeMember}
                      />
                    )
                  }
                  disabledCheckbox={
                    (!pPermissions.page_modify_user_permissions &&
                      !wPermissions.workspace_modify_user_permissions_page) ||
                    isCurrentUser ||
                    isOwner ||
                    disabled
                  }
                  selectedElements={selectedElements}
                  onSelectElements={setSelectedElements}
                  item={member}
                />
              </Fragment>
            </div>
          );
        })}
      <ManageUserPermissionsDrawer
        permissions={pagePermissions}
        open={manageUser !== null}
        onClose={() => setManageUser(null)}
        selectedUser={manageUser}
        getPermissionID={handleGetPermissionID}
        type="Page"
        updatePermissions={handleUpdateUser}
        updatingPermissions={updatingUser}
        updatePermissionsError={updatePageUserError}
        supportTargetCloudID={undefined} // This is NOT a support request.
      />
      <TransferOwnershipDrawer
        open={transferUser !== null}
        onClose={() => setTransferUser(null)}
        newUser={transferUser!}
        type="Page"
        permissions={pagePermissions}
        assignOwnership={handleTransferOwnership}
        assigningOwnership={transferringOwnership}
        assignOwnershipError={transferOwnershipError}
      />
      <BulkdUsersPermissionsDrawer
        open={!!bulkPermissions?.length}
        onClose={() => setBulkPermissions([])}
        bulkUpdate={handleBulkUsers}
        updatingBulkUsers={updating}
        updateBulkUsersError={updateBulkError}
        selectedMembers={bulkPermissions}
        pagePeople={pagePeople}
        permissions={pagePermissions}
        onSuccess={() => {
          setBulkPermissions([]);
          setSelectedElements([]);
        }}
      />
      {!!selectedElements?.length && (
        <SharedUserPermissionsFloatingToolbar
          selectedMembers={selectedElements}
          bulkManagePermissions={setBulkPermissions}
          bulkDeleteMembers={() => {
            bulkPageUsers({
              workspaceID: workspaceID ?? "",
              pageID: page?.id ?? "",
              payload: {
                UserIDs: selectedElements,
                Type: "Delete",
              },
              onSuccess: () => {
                setMessage({
                  message: "Successfully Deleted Users",
                  type: "success",
                });
                setSelectedElements([]);
              },
            });
          }}
          onClear={() => setSelectedElements([])}
        />
      )}
    </>
  );
};

const SharedUserPermissionsFloatingToolbar = ({
  onClear,
  selectedMembers,
  bulkManagePermissions,
  bulkDeleteMembers,
}: {
  onClear: () => void;
  selectedMembers: string[];
  bulkManagePermissions: (members: string[]) => void;
  bulkDeleteMembers: (members: string[]) => void;
}) => {
  const length = selectedMembers?.length;

  return (
    <FloatingToolbar disableSidebarCheck={true}>
      <ActionsCloseButton onClear={onClear} length={length} />
      <div
        className="tw:flex tw:align-middle tw:ml-2"
        style={{ overflowX: "auto" }}
      >
        <IconAction
          onClick={() => bulkManagePermissions(selectedMembers)}
          icon="pencil"
          title="Permissions"
          isMobile={true}
        />
        <IconAction
          onClick={() => bulkDeleteMembers(selectedMembers)}
          icon="delete"
          title="Remove"
          isMobile={true}
        />
      </div>
    </FloatingToolbar>
  );
};

const PageSettingsDrawer = ({
  open,
  onClose,
  page,
}: {
  open: boolean;
  onClose: () => void;
  page: PageSettingsPage;
}) => {
  const [pagePayload, setPagePayload] = useState<any>(null);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const { pPermissions } = usePagePermissions((page as Page)?.id);
  const { updatePageSettings, updateSubmitting } = useUpdatePageSettings();
  const { deletePage, pageDeleting } = useDeletePage();
  const [successMessage, setSuccessMessage] = useState("");
  const [resetData, setResetData] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("views");
  const [inviteUser, toggleInviteUser] = useToggle();
  const isLoading = page && "__Loading" in page;
  const notFound = page && "__NotFound" in page;
  const disabled = !page || isLoading || notFound;
  const isGlobalCloud = useAtomValue(isGlobalCloudAtom);
  const tenantID = useAtomValue(tenantIDAtom);

  const isInstalledGlobalApp =
    !disabled && !!page?.GlobalAppID && !isGlobalCloud;

  const handleClose = () => {
    setResetData(true);
    setUploading(false);
    setSuccessMessage("");
    onClose();
  };

  const handleDeletePage = () => {
    if (disabled) return;

    deletePage({
      pageID: page.id ?? "",
      onSuccess: handleClose,
      isApp: page.isApp,
    });
  };

  const modifyPage = async () => {
    if (disabled) return;

    setUploading(true);

    updatePageSettings({
      pageID: page.id ?? "",
      payload: {
        payload: {
          Name: pagePayload?.Name,
          Description: pagePayload.Description,
          Privacy: pagePayload.Privacy,
          ...(pagePayload?.hasOwnProperty("IconURL")
            ? { IconURL: pagePayload.IconURL }
            : { IconURL: page?.IconURL ? page?.IconURL : "" }),
        },
        _UserActivity: {
          WorkspaceID: workspaceID,
          Activity: {
            id: StrUtils.uuidv4(),
            Type: "edited-page",
            Page: {
              id: page.id,
              Name: page.Name,
              Logo: page.Icon,
            },
          },
        },
      },
      onSuccess: () => {
        setUploading(false);
        setSuccessMessage("Page uploaded successfully.");
      },
    });
  };

  return (
    <>
      <AppDrawer
        open={open}
        onClose={handleClose}
        headerTitle={
          isLoading
            ? "Loading Page..."
            : notFound
            ? "Page Not Found"
            : page?.Name
        }
        headerDescription={!disabled && page?.Description}
        headerOptions={
          <>
            {tab === "settings" && (
              <Button
                color="primary"
                size="sm"
                onClick={modifyPage}
                disabled={
                  (!cPermissions.cloud_modify_page_settings &&
                    !wPermissions.workspace_modify_page_settings &&
                    !pPermissions.page_modify_page_settings) ||
                  updateSubmitting ||
                  uploading ||
                  !pagePayload
                }
                style={{ width: 65 }}
              >
                {updateSubmitting || uploading ? (
                  <AppSpinner color="white" />
                ) : (
                  "Save"
                )}
              </Button>
            )}
            {tab === "members" && (
              <Button
                color="primary"
                size="sm"
                onClick={toggleInviteUser}
                disabled={
                  (!pPermissions.page_add_user &&
                    !cPermissions.cloud_modify_page &&
                    !wPermissions.workspace_modify_page) ||
                  isInstalledGlobalApp
                }
              >
                Invite User
              </Button>
            )}
          </>
        }
        bodyCSS={"p-0"}
        body={
          <>
            {notFound && (
              <div className="h-100 w-100 d-flex flex-column justify-content-center align-items-center">
                <h2>This page no longer exists.</h2>
                <Button color="primary" onClick={handleDeletePage}>
                  Delete From Sidebar
                </Button>
              </div>
            )}
            {isLoading && (
              <div className="h-100 w-100 d-flex justify-content-center align-items-center">
                <AppSpinner size="xl" />
              </div>
            )}
            {!isLoading && !notFound && (
              <>
                {/**@ts-ignore */}
                <AppNavbar tabs={tabs} activeTab={tab} onSelect={setTab} />
                <div className="mx--4">
                  <hr className="m-0 p-0" />
                </div>
                <div className="mt-4">
                  {tab === "views" && !disabled && (
                    <PageViews
                      views={page?.Views}
                      pageID={page?.id ?? ""}
                      isInstalledGlobalApp={isInstalledGlobalApp}
                    />
                  )}
                  {tab === "members" && !disabled && (
                    <PageMembers
                      page={page}
                      pPermissions={pPermissions}
                      wPermissions={wPermissions}
                      disabled={isInstalledGlobalApp}
                      cloudID={tenantID!}
                    />
                  )}
                  {tab === "settings" && (
                    <>
                      {isInstalledGlobalApp && (
                        <Alert color="danger" className="font-weight-bold">
                          This page is apart of a global app. It can't be
                          updated!
                        </Alert>
                      )}
                      <PageEditor
                        onChange={setPagePayload}
                        resetData={resetData}
                        setResetData={setResetData}
                        existingPage={page}
                        disabled={isInstalledGlobalApp}
                        onSubmit={() => {}}
                      />
                      <hr />
                      <h4 className="mb-0">Manage Page</h4>
                      <p>
                        <small>
                          Delete the page and all of the views inside.
                        </small>
                      </p>
                      <Button
                        className="mb-4"
                        color="danger"
                        disabled={
                          isInstalledGlobalApp ||
                          (!pPermissions.page_delete_page &&
                            !wPermissions.workspace_delete_page &&
                            !cPermissions.cloud_delete_page) ||
                          pageDeleting
                        }
                        onClick={handleDeletePage}
                      >
                        {pageDeleting ? "Deleting..." : "Delete Page"}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </>
        }
        success={
          successMessage
            ? {
                message: successMessage,
                onClear: () => setSuccessMessage(""),
              }
            : null
        }
      />
      <InvitePageUserDrawer
        open={inviteUser}
        onClose={toggleInviteUser}
        page={page}
        onSuccess={() => {}}
      />
    </>
  );
};

export default PageSettingsDrawer;
